import { Product } from "@/api/product-api";
import { cn } from "@/libs/utils";
import { useCartStore } from "@/store/cart-store";
import { Accessory } from "@/store/useBuyTactSuit";
import { useTrans } from "@/utils/useTrans";
import { QuantityCounter } from "@/v2-components/cart-sheet/quantity-counter";
import { NotifyMe } from "@/v2-components/pages/tactsuit/NotifyMe";
import TactSuitCard from "@/v2-components/TactSuitCard";
import { SetDealRailPrice } from "./set-deal";

export function Accessories({
  accessories,
  selectedAccessories,
  onChange,
  className,
  maxVisibleItems,
}: {
  selectedAccessories: Accessory[];
  onChange: (selected: Omit<Accessory, "type">) => void;
  accessories: Product[];
  className?: string;
  maxVisibleItems?: number;
}) {
  const cartItems = useCartStore((s) => s.items);
  let itemCount = 0;

  return (
    <div className={cn("flex flex-col gap-y-3", className)}>
      {accessories.map((product, index) => {
        const skuOptions = product.skuOptions;

        return skuOptions.map((skuOption) => {
          if (maxVisibleItems !== undefined && itemCount >= maxVisibleItems)
            return null;
          itemCount++;
          const found = selectedAccessories?.find(
            (a) => a.sku === skuOption.sku,
          );
          const cartItem = cartItems.find((item) => item.sku === skuOption.sku);
          const foundQuantity = found?.quantity ?? 0;
          const cartItemQuantity = cartItem?.quantity ?? 0;
          const currentQuantity = cartItemQuantity + foundQuantity;
          const canIncrease = currentQuantity < 4;
          const canDecrease = currentQuantity > 1 && foundQuantity > 0;
          const stock = skuOption.stock ?? 0;

          const toggle = () => {
            if (!found) {
              onChange({
                sku: skuOption.sku,
                slug: product.slug,
                quantity: 1,
                selected: true,
              });
            } else {
              const nextSelected = !found.selected;
              const nextQuantity = !nextSelected
                ? 1
                : found.quantity === 0
                  ? 1
                  : found.quantity;
              onChange({
                ...found,
                quantity: nextQuantity,
                selected: nextSelected,
              });
            }
          };
          const unavailable = !canIncrease && !canDecrease;

          return (
            <div key={skuOption.sku} className={cn("relative")}>
              <div
                onClick={toggle}
                // ⚠️ **키보드로 담을 수 있어야 한다.** 예전엔 맨몸 `<div onClick>` 이라
                // 카드 자체에 포커스가 안 갔고, 카드 안 유일한 focusable 이
                // `QuantityCounter` 의 +/− 였다. 그 `+` 가 `selected: true` 로 만들어줘서
                // "보이지 않는 버튼으로 Tab → Enter" 라는 경로가 **우연히** 존재했는데,
                // 그걸 `inert` 로 막으면서 키보드·스위치 사용자의 액세서리/배지 구매
                // 경로가 0이 됐다(WCAG 2.1.1 Level A, 매출 경로).
                // 카드를 진짜 컨트롤로 만들면 아래 수량 컨트롤의 `inert` 도 옳아진다 —
                // 수량은 선택 후에만 의미가 있으니까.
                role="checkbox"
                aria-checked={found?.selected ?? false}
                aria-disabled={unavailable || undefined}
                tabIndex={unavailable ? -1 : 0}
                onKeyDown={(e) => {
                  // ⚠️ **자손에서 올라온 키는 무시한다.** 선택된 카드는 `inert` 가 풀려
                  // 수량 +/− 가 다시 focusable 인데, 거기서 누른 Enter 가 여기까지
                  // 버블링되면 `preventDefault()` 가 버튼의 활성화를 죽이고
                  // `toggle()` 이 카드를 **선택 해제**한다 — 수량을 올리려던 동작이
                  // 주문에서 항목을 빼버린다. (`QuantityCounter` 는 click 만
                  // `stopPropagation` 하고 keydown 은 그대로 올려보낸다)
                  if (e.target !== e.currentTarget) return;
                  if (e.key !== "Enter" && e.key !== " ") return;
                  e.preventDefault(); // Space 스크롤 방지
                  if (!unavailable) toggle();
                }}
                className={cn(
                  "cursor-pointer rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-60",
                  unavailable && "pointer-events-none opacity-30",
                )}
              >
                <DeviceCard
                  selected={found?.selected ?? false}
                  quantity={found?.quantity ?? 1}
                  canDecrease={canDecrease}
                  canIncrease={canIncrease}
                  onDecrease={() => {
                    const nextQuantity = (found?.quantity ?? 1) - 1;
                    onChange({
                      sku: skuOption.sku,
                      slug: product.slug,
                      quantity: nextQuantity,
                      selected: nextQuantity === 0 ? false : true,
                    });
                  }}
                  onIncrease={() => {
                    onChange({
                      sku: skuOption.sku,
                      slug: product.slug,
                      quantity: (found?.quantity ?? 1) + 1,
                      selected: true,
                    });
                  }}
                  product={{ ...product, skuOptions: [skuOption] }}
                  key={skuOption.sku}
                />
              </div>
              {!stock && (
                <UnavailableItem
                  productSku={skuOption.sku}
                  productSlug={product.slug}
                />
              )}
            </div>
          );
        });
      })}
    </div>
  );
}

const DeviceCard = ({
  product,
  quantity = 0,
  onIncrease,
  onDecrease,
  canIncrease = true,
  canDecrease = true,
  selected = false,
}: {
  selected?: boolean;
  quantity?: number;
  product: Product;
  onIncrease: () => void;
  onDecrease: () => void;
  canIncrease?: boolean;
  canDecrease?: boolean;
}) => {
  return (
    <>
      <TactSuitCard product={product}>
        {/* ⚠️ 클릭 처리는 **호출처의 래퍼**가 갖는다. 예전엔 여기 `onClick` prop 이
            선언돼 있었지만 아무도 넘기지 않았다 — 나중에 카드 클릭 동작을 붙이려는
            사람이 그 훅에 연결했다가 래퍼의 `toggle()` 과 이중 발화하게 된다. */}
        <div
          className={cn(
            "group w-full overflow-hidden rounded-lg border border-coolgray-20 bg-white p-4 transition-colors",
            // ⚠️ 선택 표시는 **박스 안쪽**에 그려야 한다. 바깥으로 나가는
            // `ring-2`(box-shadow)를 래퍼에 걸었더니 `AccordionContent` 의
            // `overflow-hidden`(접힘 애니메이션용)에 좌우가 잘렸다.
            // border 1px + inset ring 1px = 시각적 2px 파랑, 잘림·레이아웃 점프 0
            // (④ Upgrade 카드와 같은 방식).
            // 참고: 예전 `ring-blue-500` 은 Tailwind 기본 파랑(#3B82F6)이었다 —
            // blue 팔레트(10~100)를 extend 로 얹은 구조라 기본 팔레트 값이 살아남아
            // 무효 클래스가 아니라 조용히 브랜드 밖 색으로 칠해진다.
            selected && "border-blue-60 ring-1 ring-inset ring-blue-60",
          )}
        >
          <div className="flex items-center gap-x-4">
            <div className="relative w-full max-w-[114px]">
              <div className="relative aspect-square w-full">
                <TactSuitCard.Thumbnail
                  fill
                  src={product.skuOptions[0]?.thumbnail}
                />
              </div>
            </div>
            <div className="flex grow flex-col gap-y-3">
              <TactSuitCard.Name
                className="text-lg font-600 tracking-[0.5px]"
                name={product.skuOptions[0].name}
              />
              <TactSuitCard.Stock className="text-base font-400" />
              {/* 피그마는 ④ Upgrade 카드와 같은 `Save $N` 배지 구성이다
                  (I28715:139636;21876:52934). `TactSuitCard.PriceDetail` 은
                  `N% off` 배지라 문구가 갈렸었다. */}
              {/* 세트 거래가 있는 단품(MOLLE Pouch)은 수량에 맞춰 세트가를 보여준다.
                  없으면 안에서 `RailCardPrice` 그대로 — 산식·근거는 `set-deal.tsx`. */}
              <SetDealRailPrice
                product={product}
                quantity={quantity}
                selected={selected}
              />
              {/* 미선택 카드의 수량 컨트롤은 자리만 잡아두고 감춘다(레이아웃 밀림 방지).
                  ⚠️ `opacity-0 pointer-events-none` 은 **클릭만** 막는다 — Tab 포커스는
                  그대로 간다. 실측: `#buy-rail` 안에 보이지 않는 focusable 이 7개
                  (수량 input 3 + 버튼 4) 잡혔다. 액세서리 수만큼 유령 탭 스톱이 늘어난다.
                  React 19 boolean `inert` 로 포커스까지 뺀다. */}
              <div
                inert={!selected}
                className={cn(
                  "flex justify-end",
                  !selected && "pointer-events-none opacity-0",
                )}
              >
                <QuantityCounter
                  value={quantity}
                  decreaseDisabled={!canDecrease}
                  increaseDisabled={!canIncrease}
                  onDecrease={onDecrease}
                  onIncrease={onIncrease}
                />
              </div>
            </div>
          </div>
        </div>
      </TactSuitCard>
    </>
  );
};

const UnavailableItem = ({
  productSlug,
  productSku,
}: {
  productSlug: string;
  productSku?: string;
}) => {
  const { t } = useTrans(["gnb"]);
  return (
    <div className="absolute inset-0 z-10 size-full overflow-hidden rounded-lg border border-coolgray-20">
      <div className="relative flex size-full flex-col items-center justify-center gap-y-3 bg-white/80">
        <p className="text-base font-400 text-black">{t("Unavailable Item")}</p>
        <NotifyMe
          productSku={productSku}
          className={cn("px-6 text-white")}
          productSlug={productSlug}
          variant={"darkgray-fill"}
          title={t("Notify When Available")}
        />
      </div>
    </div>
  );
};
